import clsx from 'clsx'
import type * as React from 'react'
import { type TrackConfig, trackInteraction } from './tracking'

// Props interfaces
interface DataTableRootProps extends React.ComponentPropsWithoutRef<'table'> {
  className?: string
}

interface DataTableHeaderProps extends React.ComponentPropsWithoutRef<'thead'> {
  className?: string
}

interface DataTableBodyProps extends React.ComponentPropsWithoutRef<'tbody'> {
  className?: string
}

interface DataTableRowProps extends React.ComponentPropsWithoutRef<'tr'> {
  className?: string
}

export interface DataTableHeaderCellProps
  extends Omit<React.ComponentPropsWithoutRef<'th'>, 'onClick' | 'onKeyDown'> {
  sortable?: boolean
  sortDirection?: 'asc' | 'desc' | null
  onSort?: () => void
  className?: string
  track?: TrackConfig<{ sortDirection: 'asc' | 'desc' }>
}

interface DataTableCellProps extends React.ComponentPropsWithoutRef<'td'> {
  label?: string // For mobile card view labels
  className?: string
}

interface DataTableFooterProps extends React.ComponentPropsWithoutRef<'div'> {
  className?: string
}

// DataTable Root - Main table wrapper
export function DataTableRoot({ className, children, ...props }: DataTableRootProps) {
  return (
    <table
      className={clsx(
        'w-full border-collapse',
        // Mobile: Display as block for card view
        'max-md:block',
        className
      )}
      {...props}
    >
      {children}
    </table>
  )
}

// DataTable Header - Table header wrapper
export function DataTableHeader({ className, children, ...props }: DataTableHeaderProps) {
  return (
    <thead
      className={clsx(
        'bg-sand-25 uppercase text-charcoal-50 text-xs font-light border-b border-gray-stroke',
        // Mobile: Hide header row in card view
        'max-md:hidden',
        className
      )}
      {...props}
    >
      {children}
    </thead>
  )
}

// DataTable Body - Table body wrapper
export function DataTableBody({ className, children, ...props }: DataTableBodyProps) {
  return (
    <tbody
      className={clsx(
        // Mobile: Display as block for card view
        'max-md:block',
        className
      )}
      {...props}
    >
      {children}
    </tbody>
  )
}

// DataTable Row - Table row with alternating colors
export function DataTableRow({ className, children, ...props }: DataTableRowProps) {
  return (
    <tr
      className={clsx(
        'even:bg-sand-25 border-b border-gray-stroke',
        // Mobile: Card-style row
        'max-md:block max-md:mb-4 max-md:border max-md:border-gray-stroke max-md:rounded-lg max-md:p-4 max-md:bg-white max-md:even:bg-white',
        className
      )}
      {...props}
    >
      {children}
    </tr>
  )
}

// DataTable HeaderCell - Header cell with sort indicators
export function DataTableHeaderCell({
  sortable = false,
  sortDirection = null,
  onSort,
  className,
  children,
  track,
  ...props
}: DataTableHeaderCellProps) {
  const nextSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'

  const handleSort = () => {
    onSort?.()
    if (sortable && onSort) trackInteraction(track, { sortDirection: nextSortDirection })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (sortable && onSort && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      handleSort()
    }
  }

  const ariaSort =
    sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined

  const sortIcon =
    sortable &&
    (sortDirection === 'asc' ? (
      <i className="fas fa-caret-up"></i>
    ) : sortDirection === 'desc' ? (
      <i className="fas fa-caret-down"></i>
    ) : (
      <i className="fas fa-sort"></i>
    ))

  return (
    <th
      {...props}
      scope="col"
      aria-sort={ariaSort}
      className={clsx(
        'text-left px-4 py-2 font-semibold',
        sortable && 'cursor-pointer select-none',
        className
      )}
      onClick={sortable && onSort ? handleSort : undefined}
      onKeyDown={sortable ? handleKeyDown : undefined}
      tabIndex={sortable ? 0 : undefined}
      role={sortable ? 'button' : undefined}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span>{children}</span>
        {sortIcon}
      </div>
    </th>
  )
}

// DataTable Cell - Data cell with mobile label support
export function DataTableCell({ label, className, children, ...props }: DataTableCellProps) {
  return (
    <td
      data-label={label}
      className={clsx(
        'px-4 py-4 text-charcoal',
        // Mobile: Flex layout with label
        'max-md:flex max-md:justify-between max-md:py-2 max-md:px-0',
        'max-md:border-b max-md:border-gray-stroke last:max-md:border-b-0',
        // Mobile label styling
        'max-md:before:content-[attr(data-label)] max-md:before:font-semibold max-md:before:text-charcoal-75 max-md:before:mr-4',
        className
      )}
      {...props}
    >
      {children}
    </td>
  )
}

// DataTable Footer - Footer area for pagination and table actions
export function DataTableFooter({ className, children, ...props }: DataTableFooterProps) {
  return (
    <div
      className={clsx(
        'flex justify-end border-t border-gray-stroke px-4 py-3',
        // If the footer immediately follows a table with body rows, the last row already
        // provides the separator. Drop the footer border to avoid a doubled line.
        '[table:has(tbody_tr)+&]:border-t-0',
        'max-md:justify-center',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// Convenient aliases
export const Root = DataTableRoot
export const Header = DataTableHeader
export const Body = DataTableBody
export const Row = DataTableRow
export const HeaderCell = DataTableHeaderCell
export const Cell = DataTableCell
export const Footer = DataTableFooter
